import { IValidationSummary } from 'ui-toolkit';
import { INewOrderSchema } from '../validation/NewOrderSchema';
import { IPartySchema, ISignatorySchema } from '../validation/PartySchema';
import { getIn } from 'formik';
import { IMobilityGroupSchema } from '../validation/MobilitySchema';
import { IAddressSchema } from '../validation/AddressSchema';
import { DisplayPartyPurpose, ePartyPurpose } from '../../../ui-toolkit/enums/ePartyPurpose';

const lenderKeys: Array<keyof INewOrderSchema> = ['lenderCompanyId'];
const clientKeys: Array<keyof INewOrderSchema> = ['clientCompanyId'];
const transactionKeys: Array<keyof INewOrderSchema> = [
  'loanId',
  'transactionCategory',
  'loanAmount',
  'estimatedPropertyValue',
  'purchasePrice',
  'closingDate',
  'estimatedClosingDate',
];
const propertyKeys: Array<keyof IAddressSchema> = ['streetAddress', 'unit', 'city', 'stateAbbreviation', 'zipCode'];

const groupKeys: Array<keyof IMobilityGroupSchema> = ['id', 'externalId', 'programType', 'takeTitle'];

export const partyKeys: Array<keyof IPartySchema> = [
  'firstName',
  'middleName',
  'lastName',
  'entityName',
  'entityType',
  'ssn',
  'maritalStatus',
  'email',
  'phoneNumber',
];
export const signatoryKeys: Array<keyof ISignatorySchema> = ['firstName', 'middleName', 'lastName'];

const prettierKeys = {
  lenderCompanyId: 'Lender',
  loanId: 'Loan Number',
  transactionCategory: 'Transaction Category',
  loanAmount: 'Loan Amount',
  estimatedPropertyValue: 'Estimated Property Value',
  purchasePrice: 'Purchase Price',
  closingDate: 'Closing Date',
  estimatedClosingDate: 'Estimated Closing Date',
  streetAddress: 'Street Address',
  unit: 'Unit',
  city: 'City',
  stateAbbreviation: 'State',
  zipCode: 'Zip Code',
  firstName: 'First Name',
  lastName: 'Last Name',
  ssn: 'Last 4 Digits Of SSN',
  email: 'Email',
  phoneNumber: 'Phone Number',
  entityName: 'Entity Name',
  entityType: 'Entity Type',
};
const prettier = (x: string) => {
  const lastKey = x.split('.').pop()!;
  return prettierKeys[lastKey] || lastKey;
};

type ErrorsType<T> = { [K in keyof T]?: T[K] extends object ? ErrorsType<T[K]> : string };

const getValidationResultsFromKeys =
  (errors: ErrorsType<INewOrderSchema>) =>
  (name: string, message: string, keys: Array<string>): IValidationSummary[] => {
    const items = keys
      .filter((t) => getIn(errors, t))
      .map((t) => ({
        name: t,
        message: `${prettier(t)}: ${getIn(errors, t)}`,
        items: [],
      })) as IValidationSummary[];
    if (items.length) {
      return [{ name, message, items }];
    }
    return [];
  };

const signatoryTransform =
  (errors: ErrorsType<INewOrderSchema>, values: INewOrderSchema) =>
  (partyIndex: number): IValidationSummary[] => {
    const baseResultMapper = getValidationResultsFromKeys(errors);
    const partyErrors = errors.parties!;
    const thisPartyErrors = partyErrors[partyIndex]! as ErrorsType<IPartySchema>;
    if (!!thisPartyErrors.signatories) {
      const signatoryResults: IValidationSummary[] = [];
      for (let index = 0; index < thisPartyErrors.signatories.length; index++) {
        const thisSignatoryErrors = thisPartyErrors.signatories[index];
        if (thisSignatoryErrors) {
          signatoryResults.push(
            ...baseResultMapper(
              `Signatory ${index + 1}`,
              `Signatory ${index + 1} has some invalid fields`,
              signatoryKeys.map((t) => `parties.${partyIndex}.signatories.${index}.${t}`)
            )
          );
        }
      }
      return signatoryResults;
    }

    return [];
  };

const partyTypeTransform =
  (errors: ErrorsType<INewOrderSchema>, values: INewOrderSchema) => (purpose: ePartyPurpose) => {
    const baseResultMapper = getValidationResultsFromKeys(errors);
    const parties = values.parties;
    const partyErrors = errors.parties!; // We check for not available outside of here.

    const purposeItems: IValidationSummary[] = [];
    // Borrowers first
    // We are starting with a long form pattern, it will be refactored once working to conform to the tests.
    let count = 0;
    if (!!parties) {
      for (let index = 0; index < parties.length; index++) {
        if (parties[index].purpose === purpose) {
          // Starts at 0, so first one sets it to 1, which is what we want to show.
          count++;
          if (partyErrors[index]) {
            // There are errors! Start with fields.
            let partyResult = baseResultMapper(
              `${DisplayPartyPurpose[purpose]} ${count}`,
              `${DisplayPartyPurpose[purpose]} ${count} has some invalid fields`,
              partyKeys.map((t) => `parties.${index}.${t}`)
            );
            const signatoryResults = signatoryTransform(errors, values)(index);
            if (partyResult.length || signatoryResults.length) {
              // One of them has length, so make sure Party Result has something.
              partyResult = [
                {
                  name: `${DisplayPartyPurpose[purpose]} ${count}`,
                  message: `${DisplayPartyPurpose[purpose]} ${count} has some invalid fields`,
                  items: [...(partyResult[0] ? partyResult[0].items : []), ...signatoryResults],
                },
              ];
            }
            purposeItems.push(...partyResult);
          }
        }
      }
    }
    if (purposeItems.length) {
      return [
        {
          name: `${DisplayPartyPurpose[purpose]}(s) Information`,
          message: `${DisplayPartyPurpose[purpose]}(s) Information has some invalid fields`,
          items: purposeItems,
        },
      ];
    }
    return [];
  };

const partyTransform = (errors: ErrorsType<INewOrderSchema>, values: INewOrderSchema): IValidationSummary[] => {
  const partyErrors = errors.parties;

  if (!partyErrors) {
    return [];
  }

  const partyChecker = partyTypeTransform(errors, values);

  return [
    ...partyChecker(ePartyPurpose.borrower),
    ...partyChecker(ePartyPurpose.buyer),
    ...partyChecker(ePartyPurpose.seller),
  ];
};

const groupTransform = (errors: ErrorsType<INewOrderSchema>, values: INewOrderSchema): IValidationSummary[] => {
  const mobilityGroupErrors = errors.mobilityGroup;
  const mobilityErrors = errors.mobility;
  if (!mobilityGroupErrors && !mobilityErrors) {
    return [];
  }
  const baseResultMapper = getValidationResultsFromKeys(errors);

  return [
    ...baseResultMapper(
      'Group Information',
      'Group Information has some invalid fields',
      groupKeys.map((t) => `mobilityGroup.${t}`).concat(`mobility.transactionType`)
    ),
  ];
};

export const transformFormikValidation = (errors: any, values: INewOrderSchema): IValidationSummary[] => {
  const baseResultMapper = getValidationResultsFromKeys(errors);
  return [
    ...baseResultMapper('Client Information', 'Client Information has an invalid field', clientKeys),
    ...baseResultMapper('Lender Information', 'Lender Information has an invalid field', lenderKeys),
    ...baseResultMapper('Transaction Information', 'Transaction Information has some invalid fields', transactionKeys),
    ...baseResultMapper(
      'Property Information',
      'Property Information has some invalid fields',
      propertyKeys.map((t) => `propertyAddress.${t}`)
    ),
    ...partyTransform(errors, values),
    ...groupTransform(errors, values),
  ];
};
